import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { customerService } from '@services/api/customerService';
import { useAuthStore } from '@stores/authStore';
import { OverviewTab } from '@components/customers/tabs/OverviewTab';
import { AccountsTab } from '@components/customers/tabs/AccountsTab';
import { CardsTab } from '@components/customers/tabs/CardsTab';
import { TransactionsTab } from '@components/customers/tabs/TransactionsTab';
import { PaymentControlsTab } from '@components/customers/tabs/PaymentControlsTab';
import { CasesTab } from '@components/customers/tabs/CasesTab';
import { SessionsTab } from '@components/customers/tabs/SessionsTab';
import { EventsTab } from '@components/customers/tabs/EventsTab';
import { DocumentsTab } from '@components/customers/tabs/DocumentsTab';
import { ComplianceTab } from '@components/customers/tabs/ComplianceTab';
import { permissions } from '@utils/permissions';
import './CustomerDetail.css';

type TabType = 'overview' | 'accounts' | 'cards' | 'transactions' | 'payment_controls' | 'cases' | 'sessions' | 'events' | 'documents' | 'compliance';

export const CustomerDetail = () => {
  const { id } = useParams();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [customer, setCustomer] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id && user) {
      loadCustomer();
    }
  }, [id, user]);

  const loadCustomer = async () => {
    if (!id || !user) return;
    
    setLoading(true);
    const result = await customerService.getCustomerById(id, user.id);
    
    if (result.success && result.data) {
      setCustomer(result.data as any);
    }
    
    setLoading(false);
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div></div>;
  }

  if (!customer) {
    return <div>Customer not found</div>;
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'accounts', label: 'Accounts' },
    { id: 'cards', label: 'Cards' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'payment_controls', label: 'Payment Controls & Beneficiaries' },
    { id: 'cases', label: 'Cases' },
    { id: 'sessions', label: 'Sessions' },
    { id: 'events', label: 'Errors & Warnings' },
    { id: 'documents', label: 'Documents' },
    { id: 'compliance', label: 'Compliance & Audit', hidden: !permissions.isComplianceOrAdmin(user) },
  ].filter(tab => !tab.hidden);

  return (
    <div className="customer-detail">
      <div className="customer-detail-header">
        <Link to="/customers" className="customer-back-button">
          ← Back to Customers
        </Link>
        
        <div className="customer-title">
          <h1 className="page-title">
            {customer.first_name} {customer.last_name}
          </h1>
          <span className="customer-id-badge">ID: {customer.id.slice(0, 8)}</span>
        </div>
      </div>

      <div className="tab-navigation">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'tab-button-active' : ''}`}
            onClick={() => setActiveTab(tab.id as TabType)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && <OverviewTab customer={customer} />}
        {activeTab === 'accounts' && <AccountsTab customerId={id!} />}
        {activeTab === 'cards' && <CardsTab customerId={id!} />}
        {activeTab === 'transactions' && <TransactionsTab customerId={id!} />}
        {activeTab === 'payment_controls' && <PaymentControlsTab customerId={id!} />}
        {activeTab === 'cases' && <CasesTab customerId={id!} />}
        {activeTab === 'sessions' && <SessionsTab customerId={id!} />}
        {activeTab === 'events' && <EventsTab customerId={id!} />}
        {activeTab === 'documents' && <DocumentsTab customerId={id!} />}
        {activeTab === 'compliance' && permissions.isComplianceOrAdmin(user) && (
          <ComplianceTab customerId={id!} />
        )}
      </div>
    </div>
  );
};

